
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { CodeBlock } from "@/components/api-docs/CodeBlock";

export const PricingApiDocContent = () => {
  return (
    <div className="space-y-8">
      <section className="space-y-4">
        <h2 className="text-2xl font-bold">Overview</h2>
        <p className="text-gray-700">
          The Pricing API allows you to retrieve pricing plans for products and create subscriptions.
          This API is designed to be integrated with other platforms that need to access our subscription services.
        </p>
      </section>

      <section className="space-y-4">
        <h2 className="text-2xl font-bold">Authentication</h2>
        <p className="text-gray-700">
          All API requests require an API key that should be included in the request header:
        </p>
        <CodeBlock
          language="http"
          code={`x-api-key: YOUR_API_KEY`}
          title="API Key Header"
        />
      </section>

      <Tabs defaultValue="get-pricing">
        <TabsList>
          <TabsTrigger value="get-pricing">Get Pricing Plans</TabsTrigger>
          <TabsTrigger value="create-subscription">Create Subscription</TabsTrigger>
          <TabsTrigger value="get-school-billing">Get School Billing Data</TabsTrigger>
        </TabsList>
        
        <TabsContent value="get-pricing" className="space-y-6">
          <section className="space-y-4">
            <h2 className="text-2xl font-bold">Get Pricing Plans</h2>
            <p className="text-gray-700">
              Retrieve available pricing plans for a specific product, including any applicable discounts
              for a specific school.
            </p>
            
            <div className="space-y-2">
              <h3 className="text-xl font-semibold">Endpoint</h3>
              <CodeBlock
                language="http"
                code={`POST https://swhwykzorcbiutczhjgn.functions.supabase.co/get-pricing-plans`}
                title="Endpoint"
              />
            </div>
            
            <div className="space-y-2">
              <h3 className="text-xl font-semibold">Request</h3>
              <CodeBlock
                language="json"
                code={`{
  "schoolId": "12345",
  "productName": "Class Management"
}`}
                title="Request Body"
              />
              
              <p className="text-gray-700">
                <strong>Parameters:</strong>
              </p>
              <ul className="list-disc pl-6 space-y-1 text-gray-700">
                <li>
                  <code className="bg-gray-100 text-red-600 p-1 rounded">schoolId</code> 
                  <span className="text-sm ml-2">(required)</span> - The school's unique ID
                </li>
                <li>
                  <code className="bg-gray-100 text-red-600 p-1 rounded">productName</code> 
                  <span className="text-sm ml-2">(required)</span> - Name of the product to get pricing for
                </li>
              </ul>
            </div>
            
            <div className="space-y-2">
              <h3 className="text-xl font-semibold">Response</h3>
              <CodeBlock
                language="json"
                code={`{
  "product": {
    "id": "550e8400-e29b-41d4-a716-446655440000",
    "name": "Class Management",
    "features": "Student attendance, class scheduling, etc.",
    "description": "Complete class management system"
  },
  "plans": [
    {
      "plan_id": "33e8400-e29b-41d4-a716-4466554499",
      "plan_name": "Monthly",
      "original_price": 359000,
      "discount": {
        "type": "percentage",
        "value": 10,
        "amount": 35900
      },
      "price_after_discount": 323100,
      "tax_rate": 11,
      "tax_amount": 35541,
      "final_amount": 358641,
      "duration": 30,
      "type": "Monthly"
    },
    {
      "plan_id": "33e8401-e29b-41d4-a716-4466554499",
      "plan_name": "Yearly",
      "original_price": 1499000,
      "discount": null,
      "price_after_discount": 1499000,
      "tax_rate": 11,
      "tax_amount": 164890,
      "final_amount": 1663890,
      "duration": 365,
      "type": "Yearly"
    }
  ]
}`}
                title="Success Response (200 OK)"
              />
            </div>
            
            <div className="space-y-2">
              <h3 className="text-xl font-semibold">Error Responses</h3>
              <CodeBlock
                language="json"
                code={`{
  "error": "Invalid product name",
  "status": 400
}`}
                title="Error Response (400 Bad Request)"
              />
              
              <CodeBlock
                language="json"
                code={`{
  "error": "Invalid API key",
  "status": 401
}`}
                title="Error Response (401 Unauthorized)"
              />
            </div>
          </section>
        </TabsContent>
        
        <TabsContent value="create-subscription" className="space-y-6">
          <section className="space-y-4">
            <h2 className="text-2xl font-bold">Create Subscription</h2>
            <p className="text-gray-700">
              Creates a new subscription and associated bill for a specified product/plan and school.
            </p>
            
            <div className="space-y-2">
              <h3 className="text-xl font-semibold">Endpoint</h3>
              <CodeBlock
                language="http"
                code={`POST https://swhwykzorcbiutczhjgn.functions.supabase.co/create-subscription`}
                title="Endpoint"
              />
            </div>
            
            <div className="space-y-2">
              <h3 className="text-xl font-semibold">Request</h3>
              <CodeBlock
                language="json"
                code={`{
  "productId": "550e8400-e29b-41d4-a716-446655440000",
  "planId": "33e8400-e29b-41d4-a716-4466554499",
  "schoolId": "12345",
  "userEmail": "subscriber@school.edu"
}`}
                title="Request Body"
              />
              
              <p className="text-gray-700">
                <strong>Parameters:</strong>
              </p>
              <ul className="list-disc pl-6 space-y-1 text-gray-700">
                <li>
                  <code className="bg-gray-100 text-red-600 p-1 rounded">productId</code> 
                  <span className="text-sm ml-2">(required)</span> - The product's unique ID
                </li>
                <li>
                  <code className="bg-gray-100 text-red-600 p-1 rounded">planId</code> 
                  <span className="text-sm ml-2">(required)</span> - The plan's unique ID
                </li>
                <li>
                  <code className="bg-gray-100 text-red-600 p-1 rounded">schoolId</code> 
                  <span className="text-sm ml-2">(required)</span> - The school's unique ID
                </li>
                <li>
                  <code className="bg-gray-100 text-red-600 p-1 rounded">userEmail</code> 
                  <span className="text-sm ml-2">(required)</span> - Email of the user creating the subscription
                </li>
                <li>
                  <code className="bg-gray-100 text-red-600 p-1 rounded">apiKey</code> 
                  <span className="text-sm ml-2">(optional)</span> - API key (can also be provided in header)
                </li>
              </ul>
            </div>
            
            <div className="space-y-2">
              <h3 className="text-xl font-semibold">Response</h3>
              <CodeBlock
                language="json"
                code={`{
  "success": true,
  "subscription": {
    "id": "b5d1b01d-9e99-4c09-b0b3-8f5248c6e48a",
    "status": "pending",
    "start_date": "2023-10-15T08:30:00.000Z",
    "end_date": "2023-11-14T08:30:00.000Z"
  },
  "bill": {
    "id": "67890abc-def1-2345-6789-0abcdef12345",
    "transaction_number": "ABC123",
    "amount": 358641,
    "discount": 35900,
    "tax": 35541,
    "status": "unpaid",
    "due_date": "2023-10-18T08:30:00.000Z"
  }
}`}
                title="Success Response (200 OK)"
              />
            </div>
            
            <div className="space-y-2">
              <h3 className="text-xl font-semibold">Error Responses</h3>
              <CodeBlock
                language="json"
                code={`{
  "success": false,
  "error": "Missing required fields: productId, planId, schoolId, and userEmail are required"
}`}
                title="Error Response (400 Bad Request)"
              />
              
              <CodeBlock
                language="json"
                code={`{
  "success": false,
  "error": "Invalid API key"
}`}
                title="Error Response (401 Unauthorized)"
              />
              
              <CodeBlock
                language="json"
                code={`{
  "success": false,
  "error": "Invalid plan or product ID, or plan does not belong to specified product"
}`}
                title="Error Response (400 Bad Request)"
              />
              
              <CodeBlock
                language="json"
                code={`{
  "success": false,
  "error": "School not registered with payment system: 12345"
}`}
                title="Error Response (400 Bad Request)"
              />
            </div>
            
            <div className="space-y-2">
              <h3 className="text-xl font-semibold">Notes</h3>
              <ul className="list-disc pl-6 space-y-1 text-gray-700">
                <li>This endpoint automatically applies any applicable discounts based on the school and plan.</li>
                <li>The system will validate that the school is registered with the payment provider before creating the subscription.</li>
                <li>If the email provided doesn't exist in the system, a new user account will be created automatically.</li>
                <li>The bill is created with a 3-day payment window by default.</li>
              </ul>
            </div>
          </section>
        </TabsContent>
        
        <TabsContent value="get-school-billing" className="space-y-6">
          <section className="space-y-4">
            <h2 className="text-2xl font-bold">Get School Billing Data</h2>
            <p className="text-gray-700">
              Retrieve subscription and billing information for a specific school. This endpoint provides a comprehensive view of a school's subscription status and billing history.
            </p>
            
            <div className="space-y-2">
              <h3 className="text-xl font-semibold">Endpoint</h3>
              <CodeBlock
                language="http"
                code={`POST https://swhwykzorcbiutczhjgn.functions.supabase.co/get-school-billing-data`}
                title="Endpoint"
              />
            </div>
            
            <div className="space-y-2">
              <h3 className="text-xl font-semibold">Request</h3>
              <CodeBlock
                language="json"
                code={`{
  "schoolId": "SCH123456",
  "token": "optional-access-token-if-required"
}`}
                title="Request Body"
              />
              
              <p className="text-gray-700">
                <strong>Parameters:</strong>
              </p>
              <ul className="list-disc pl-6 space-y-1 text-gray-700">
                <li>
                  <code className="bg-gray-100 text-red-600 p-1 rounded">schoolId</code> 
                  <span className="text-sm ml-2">(required)</span> - The school's unique ID
                </li>
                <li>
                  <code className="bg-gray-100 text-red-600 p-1 rounded">token</code> 
                  <span className="text-sm ml-2">(optional)</span> - Access token for schools that require authentication
                </li>
              </ul>
            </div>
            
            <div className="space-y-2">
              <h3 className="text-xl font-semibold">Response</h3>
              <CodeBlock
                language="json"
                code={`{
  "success": true,
  "data": {
    "school": {
      "id": "a1b2c3d4-e5f6-7890-abcd-1234567890ab",
      "school_id": "SCH123456",
      "nama_sekolah": "Example School"
    },
    "subscriptions": [
      {
        "id": "550e8400-e29b-41d4-a716-446655440000",
        "status": "active",
        "start_date": "2023-10-15T08:30:00.000Z",
        "end_date": "2024-10-14T08:30:00.000Z",
        "auto_renewal": true,
        "school_id": "SCH123456",
        "plan": {
          "id": "33e8400-e29b-41d4-a716-4466554499",
          "name": "Premium Annual",
          "price": 1499000,
          "duration": 365,
          "product": {
            "id": "22d7300-e29b-41d4-a716-446655448888",
            "name": "Class Management",
            "description": "Complete class management system",
            "logo": "https://example.com/logos/class-mgmt.png"
          }
        },
        "bills": [
          {
            "id": "67890abc-def1-2345-6789-0abcdef12345",
            "amount": 1499000,
            "tax": 164890,
            "discount": 149900,
            "discount_type": "percentage",
            "status": "paid",
            "due_date": "2023-10-18T08:30:00.000Z",
            "description": "Annual subscription for Class Management",
            "transaction_number": "INV-2023-10-1234",
            "plan_id": "33e8400-e29b-41d4-a716-4466554499",
            "subscription_id": "550e8400-e29b-41d4-a716-446655440000",
            "plan_price": 1499000
          }
        ]
      }
    ]
  },
  "message": "Operation completed successfully"
}`}
                title="Success Response (200 OK)"
              />
            </div>
            
            <div className="space-y-2">
              <h3 className="text-xl font-semibold">Error Responses</h3>
              <CodeBlock
                language="json"
                code={`{
  "success": false,
  "error": "Missing required parameter: schoolId"
}`}
                title="Error Response (400 Bad Request)"
              />
              
              <CodeBlock
                language="json"
                code={`{
  "success": false,
  "error": "This school requires an access token"
}`}
                title="Error Response (401 Unauthorized - Missing Token)"
              />
              
              <CodeBlock
                language="json"
                code={`{
  "success": false,
  "error": "Invalid token"
}`}
                title="Error Response (401 Unauthorized - Invalid Token)"
              />
              
              <CodeBlock
                language="json"
                code={`{
  "success": false,
  "error": "School not found"
}`}
                title="Error Response (404 Not Found)"
              />
            </div>
            
            <div className="space-y-2">
              <h3 className="text-xl font-semibold">Usage Notes</h3>
              <ul className="list-disc pl-6 space-y-1 text-gray-700">
                <li>The endpoint first checks if the school requires an access token. If a token is required but not provided, it returns a 401 error.</li>
                <li>The response includes comprehensive information about the school and all its subscriptions with related plan and product details.</li>
                <li>Bills are now nested within their corresponding subscription objects rather than in a separate array.</li>
                <li>Subscription status can be one of: "pending", "active", "expired", "cancelled", or "suspended".</li>
                <li>Bill status can be one of: "unpaid", "paid", "overdue", "cancelled", or "processing".</li>
                <li>For security reasons, this endpoint should only be used in authenticated admin interfaces or secure server-to-server communications.</li>
              </ul>
            </div>
            
            <div className="space-y-2">
              <h3 className="text-xl font-semibold">Code Example</h3>
              <CodeBlock
                language="javascript"
                code={`// Using fetch API
const fetchSchoolBillingData = async (schoolId) => {
  try {
    const response = await fetch(
      'https://swhwykzorcbiutczhjgn.functions.supabase.co/get-school-billing-data',
      {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': 'Bearer ' + supabaseAnonKey
        },
        body: JSON.stringify({ 
          schoolId, 
          token: 'optional-access-token' 
        })
      }
    );
    
    const data = await response.json();
    
    if (!data.success) {
      throw new Error(data.error);
    }
    
    return data.data;
  } catch (error) {
    console.error('Error fetching school billing data:', error);
    throw error;
  }
}`}
                title="JavaScript Example"
              />
            </div>
          </section>
        </TabsContent>
      </Tabs>
      
      <section className="space-y-4">
        <h2 className="text-2xl font-bold">Error Codes</h2>
        <table className="min-w-full bg-white border border-gray-200">
          <thead className="bg-gray-50">
            <tr>
              <th className="py-3 px-4 border-b text-left">Status Code</th>
              <th className="py-3 px-4 border-b text-left">Description</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-200">
            <tr>
              <td className="py-2 px-4">200</td>
              <td className="py-2 px-4">Success</td>
            </tr>
            <tr>
              <td className="py-2 px-4">400</td>
              <td className="py-2 px-4">Bad Request - Invalid parameters</td>
            </tr>
            <tr>
              <td className="py-2 px-4">401</td>
              <td className="py-2 px-4">Unauthorized - Invalid API key</td>
            </tr>
            <tr>
              <td className="py-2 px-4">404</td>
              <td className="py-2 px-4">Not Found - Resource not found</td>
            </tr>
            <tr>
              <td className="py-2 px-4">500</td>
              <td className="py-2 px-4">Internal Server Error</td>
            </tr>
          </tbody>
        </table>
      </section>
    </div>
  );
};
